/**
 * `world_clock` — the **local time** in the stored timezone, which is what monday's grid
 * paints, over a searchable IANA list (README: *"A searchable timezone list"*).
 *
 * `ColumnValue.text` is the identifier with underscores as spaces, not a time — the cell and
 * the text deliberately differ, and the shared folder owns both.
 */
import {
  getColumnType,
  worldClockText,
  type WorldClockSettings,
  type WorldClockValue,
} from '@elizmonday/shared';
import { useState } from 'react';
import { CellPopover, PickerSearch } from '../primitives';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { WorldClockSettingsDialog } from './SettingsDialog';

const shared = getColumnType('world_clock');

export const WORLD_CLOCK_PICKER_WIDTH = 260;

/** Every zone the browser knows; monday's own list is the IANA database too. */
function zones(): string[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  return typeof supported === 'function' ? supported('timeZone') : ['UTC'];
}

/** `hh:mm A` by default, `HH:mm` when the column says so `[09§A.7 world_clock]`. */
export function clockAt(timezone: string, format: string | undefined, now: Date): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: format !== 'HH:mm',
    }).format(now);
  } catch {
    return '';
  }
}

export const worldClockColumnUi = defineColumnUi<WorldClockValue, WorldClockSettings>({
  type: 'world_clock',
  icon: 'columnCreationLog',
  gallery: { group: 'dates', keywords: ['world clock', 'timezone', 'time', 'location'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'center',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,

  Cell: ({ value, settings }) => {
    if (value === null) return <span className="ez-cell__empty" aria-hidden="true" />;
    return (
      <span className="ez-clock" title={worldClockText(value.timezone)}>
        <span className="ez-clock__time">{clockAt(value.timezone, settings.format, new Date())}</span>
        <span className="ez-clock__zone">{worldClockText(value.timezone).split('/').pop()}</span>
      </span>
    );
  },

  Editor: ({ value, initialInput, onCommit, onCancel }) => {
    const [query, setQuery] = useState(initialInput ?? '');
    const needle = query.trim().toLowerCase();
    const options = zones()
      .filter((zone) => needle === '' || zone.toLowerCase().includes(needle))
      .slice(0, 60);
    return (
      <CellPopover
        label="Pick a timezone"
        width={WORLD_CLOCK_PICKER_WIDTH}
        /* **P-282**: the value is written by clicking an option; a dismiss writes nothing. */
        onDismiss={onCancel}
        onCancel={onCancel}
      >
        <PickerSearch value={query} onChange={setQuery} placeholder="Search timezones" />
        <ul className="ez-option-list" role="listbox" aria-label="Timezones">
          {options.map((zone) => (
            <li key={zone}>
              <button
                type="button"
                role="option"
                aria-selected={value?.timezone === zone}
                className="ez-option-list__option"
                data-testid={`timezone-option-${zone}`}
                onClick={() => onCommit({ timezone: zone })}
              >
                {worldClockText(zone)}
              </button>
            </li>
          ))}
          {options.length === 0 ? <li className="ez-option-list__empty">No matching timezone</li> : null}
        </ul>
      </CellPopover>
    );
  },

  SettingsDialog: WorldClockSettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim().replace(/ /g, '_');
    return trimmed === '' ? null : (shared.parseValue({ timezone: trimmed }, settings) as WorldClockValue | null);
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('world_clock'),
});
